import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useStores } from "stores";

import { prepareReleaseForm } from "components/ReleaseForm/helpers";

import { defaultValues } from "../../../pages/User/Releases/Release/constants";
import { schema } from "../../../pages/User/Releases/Release/validation";
import { ReleaseForm } from "../ReleaseFormHeader/types";
import { ReleaseUrlParams } from "../types";

/**
 * Custom hook to manage the state and behavior of the release form.
 *
 * This hook integrates `react-hook-form` with MobX stores and manages data
 * synchronization for releases, including artists, works, and tracks.
 *
 * @returns The `useForm` instance with form state and methods.
 */
export const useReleaseForm = () => {
  const {
    revisedFormStore: {
      release,
      resetRevisedForm,
      releaseArtists: { releaseArtistsList },
      works: { worksList },
      tracks: { tracksList },
      retrieveRelease,
    },
  } = useStores();

  // Extract `userId` and `releaseId` from the URL parameters
  const { userId = null, releaseId } = useParams<ReleaseUrlParams>();

  const form = useForm<ReleaseForm>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const { reset, setValue } = form;

  /**
   * Effect: Sync release data with the form.
   * When `release` changes, reset the form with prepared release data.
   */
  useEffect(() => {
    if (release) reset(prepareReleaseForm(release));
  }, [release, reset]);

  /**
   * Effect: Sync release artists list with the form.
   * Updates the `releaseArtists` field when `releaseArtistsList` changes.
   */
  useEffect(() => {
    if (releaseArtistsList) setValue("releaseArtists", releaseArtistsList, { shouldDirty: false });
  }, [releaseArtistsList, setValue]);

  /**
   * Effect: Sync works list with the form.
   * Updates the `works` field when `worksList` changes.
   */
  useEffect(() => {
    if (worksList) setValue("works", worksList, { shouldDirty: false });
  }, [worksList, setValue]);

  /**
   * Effect: Sync tracks list with the form.
   * Updates the `tracks` field when `tracksList` changes.
   */
  useEffect(() => {
    if (tracksList) setValue("tracks", tracksList, { shouldDirty: false });
  }, [tracksList, setValue]);

  /**
   * Effect: Retrieve release data when `releaseId` changes.
   * Fetches release data using `retrieveRelease` and resets the form state on unmount.
   */
  useEffect(() => {
    if (releaseId) retrieveRelease(userId, releaseId); // Fetch release data
    return () => resetRevisedForm(); // Cleanup: Reset the revised form state
  }, [userId, releaseId, retrieveRelease, resetRevisedForm]);

  // Return the `useForm` instance with form state and methods
  return form;
};
